import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { auth, db } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, orderBy, getDocs, addDoc, doc, updateDoc, arrayUnion } from 'firebase/firestore';

const OrdersContext = createContext();

export const useOrders = () => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrdersProvider');
  }
  return context;
};

export const OrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);

  // Sync with Firestore for authenticated user
  useEffect(() => {
    let unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUid(user.uid);
        await fetchOrders(user.uid);
      } else {
        setUid(null);
        setOrders([]);
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to fetch orders from Firestore
  const fetchOrders = async (userId) => {
    setLoading(true);
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOrders(fetched);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (items, shippingDetails, paymentInfo) => {
    const orderPayload = {
      userId: uid || null,
      items: items.map(item => ({ ...item, reviewSubmitted: false })),
      deliveryAddress: shippingDetails,
      paymentMethod: paymentInfo?.method || 'unknown',
      pricing: paymentInfo?.pricing || {},
      status: 'processing',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      orderNumber: `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
    };

    try {
      if (uid) {
        const ordersRef = collection(db, 'orders');
        const orderRef = await addDoc(ordersRef, orderPayload);
        const newOrder = { id: orderRef.id, ...orderPayload };

        // update local state
        setOrders(prev => [newOrder, ...prev]);

        // add order id to user's document
        const userRef = doc(db, 'users', uid);
        await updateDoc(userRef, { orders: arrayUnion(orderRef.id) });

        toast.success('Order placed successfully!');
        return newOrder;
      } else {
        // fallback to local order when not authenticated
        const localOrder = {
          id: `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          date: new Date().toISOString(),
          status: 'processing',
          items: items.map(item => ({ ...item, reviewSubmitted: false })),
          deliveryAddress: shippingDetails,
          paymentMethod: paymentInfo?.method || 'unknown',
        };
        setOrders(prev => [localOrder, ...prev]);
        toast.success('Order placed locally');
        return localOrder;
      }
    } catch (err) {
      console.error('Error creating order:', err);
      toast.error('Failed to place order');
      throw err;
    }
  };

  const updateOrderStatus = (orderId, status) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? { 
              ...order, 
              status,
              timeline: order.timeline.map(t => 
                t.status.toLowerCase().includes(status) 
                  ? { ...t, completed: true, date: new Date().toISOString() }
                  : t
              )
            }
          : order
      )
    );
    toast.success(`Order status updated to ${status}`);
  };

  const addReview = (orderId, productId, rating, review) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? {
              ...order,
              items: order.items.map(item =>
                item.id === productId
                  ? { ...item, reviewSubmitted: true, rating, review }
                  : item
              )
            }
          : order
      )
    );
    toast.success('Review submitted successfully!');
  };

  const cancelOrder = (orderId) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? { 
              ...order, 
              status: 'cancelled',
              timeline: order.timeline.map(t => ({ ...t, completed: false }))
            }
          : order
      )
    );
    toast.info('Order cancelled');
  };

  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  const getOrdersByStatus = (status) => {
    if (status === 'all') return orders;
    return orders.filter(order => order.status === status);
  };

  const getOrderCount = () => {
    return orders.length;
  };

  const value = {
    orders,
    loading,
    createOrder,
    updateOrderStatus,
    addReview,
    cancelOrder,
    getOrderById,
    getOrdersByStatus,
    getOrderCount,
    refreshOrders: () => uid && fetchOrders(uid),
  };

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};